import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import {
    InputLabelProps,
    InputBaseComponentProps,
    Stack,
} from '../../base-components';
import FormFieldMaxCharacterCount from '../FormFieldMaxCharacterCount';
import { StyledTextArea } from './FormTextArea.styles';

interface Props<F extends FieldValues> {
    name: Path<F>;
    control: Control<F>;
    inputLabelProps?: InputLabelProps;
    inputProps?: InputBaseComponentProps;
    disabled?: boolean;
    placeholder?: string;
    rows?: number;
    label?: string;
    defaultValue?: string;
    required?: boolean;
    className?: string;
    maxRows?: number | string;
    minRows?: number | string;
    dataTestId?: string;
    allowMoreCharactersThanMax?: boolean;
}

export const FormTextArea = <F extends FieldValues>(props: Props<F>) => {
    const {
        name,
        control,
        placeholder,
        rows = 4,
        disabled = false,
        label,
        required = false,
        inputProps,
        className,
        dataTestId,
        allowMoreCharactersThanMax = false,
        inputLabelProps,
        maxRows,
        minRows,
        defaultValue,
    } = props;

    const maxCharacterCount = inputProps?.maxLength;
    const showMaxCharacterCount = !!maxCharacterCount;
    if (maxCharacterCount && allowMoreCharactersThanMax) {
        inputProps.maxLength = null;
    }

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
                <Stack width="100%">
                    <StyledTextArea
                        rows={rows}
                        label={label ?? ''}
                        multiline
                        value={field.value}
                        defaultValue={defaultValue}
                        placeholder={placeholder}
                        required={required}
                        error={Boolean(error)}
                        InputLabelProps={inputLabelProps}
                        inputProps={inputProps}
                        variant="outlined"
                        onChange={(value) => {
                            field.onChange(value);
                        }}
                        fullWidth
                        helperText={error && error.message}
                        disabled={disabled}
                        className={className}
                        minRows={minRows}
                        maxRows={maxRows}
                        data-testid={dataTestId || name}
                    />
                    {showMaxCharacterCount && (
                        <FormFieldMaxCharacterCount
                            fieldValue={field.value}
                            maxCharacterCount={maxCharacterCount}
                        />
                    )}
                </Stack>
            )}
        />
    );
};

export default FormTextArea;
